import React, { useState, useMemo } from 'react';
import { Skill, AVAILABLE_SKILLS, SkillTemplate } from '../types';
import { useExternalSkills } from '../hooks/useExternalSkills';
import '../styles/SkillSelector.css';

interface Props {
  selectedSkills: Skill[];
  onSkillToggle: (skillId: string, skillTemplate?: SkillTemplate) => void;
}

type TabType = 'builtin' | 'external';

const SkillSelector: React.FC<Props> = ({ selectedSkills, onSkillToggle }) => {
  const [activeTab, setActiveTab] = useState<TabType>('builtin');
  const [searchQuery, setSearchQuery] = useState('');

  const {
    skills: externalSkills,
    loading: externalLoading,
    error: externalError,
    hasMore,
    searchTerm,
    setSearchTerm,
    loadMore,
    retry
  } = useExternalSkills();

  const categories: { key: Skill['category']; label: string; description: string }[] = [
    { key: 'frontend', label: 'Frontend', description: 'UI components and interactions' },
    { key: 'backend', label: 'Backend', description: 'Server-side functionality' },
    { key: 'data', label: 'Data', description: 'Data processing and visualization' },
    { key: 'ai', label: 'AI', description: 'Artificial intelligence features' },
    { key: 'integration', label: 'Integration', description: 'External service integrations' }
  ];

  // Filter built-in skills by search query
  const filteredBuiltinSkills = useMemo(() => {
    if (!searchQuery) return AVAILABLE_SKILLS;
    const lower = searchQuery.toLowerCase();
    return AVAILABLE_SKILLS.filter(
      s => s.name.toLowerCase().includes(lower) || s.description.toLowerCase().includes(lower)
    );
  }, [searchQuery]);

  const getSkillsByCategory = (category: Skill['category']): SkillTemplate[] => {
    return filteredBuiltinSkills.filter(s => s.category === category);
  };

  const getExternalSkillsByCategory = (category: Skill['category']): SkillTemplate[] => {
    return externalSkills.filter(s => s.category === category);
  };

  const isSkillSelected = (skillId: string): boolean => {
    return selectedSkills.some(s => s.id === skillId);
  };

  const getCategoryIcon = (category: Skill['category']): string => {
    switch (category) {
      case 'frontend': return '🎨';
      case 'backend': return '⚙️';
      case 'data': return '📊';
      case 'ai': return '🤖';
      case 'integration': return '🔗';
      default: return '📦';
    }
  };

  const formatInstalls = (count: number): string => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return String(count);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    // Always sync search with external skills hook for client-side filtering
    setSearchTerm(value);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Load more external skills if we have a search term and few results
    if (tab === 'external' && searchQuery && externalSkills.length < 10 && hasMore && !externalLoading) {
      loadMore();
    }
  };

  const renderSkillCard = (skill: SkillTemplate, isExternal: boolean = false) => (
    <div
      key={skill.id}
      className={`skill-card ${isSkillSelected(skill.id) ? 'selected' : ''} ${isExternal ? 'external' : ''}`}
      onClick={() => onSkillToggle(skill.id, isExternal ? skill : undefined)}
    >
      <div className="skill-header">
        <input
          type="checkbox"
          checked={isSkillSelected(skill.id)}
          onChange={() => onSkillToggle(skill.id, isExternal ? skill : undefined)}
          onClick={e => e.stopPropagation()}
        />
        <h4>{skill.name}</h4>
        {isExternal && <span className="external-badge">skills.sh</span>}
      </div>
      <p className="skill-description">{skill.description}</p>
      {isExternal && skill.installs !== undefined && (
        <div className="skill-meta">
          <span className="install-count">
            ⬇️ {formatInstalls(skill.installs)} installs
          </span>
          {skill.topSource && (
            <span className="top-source">📦 {skill.topSource}</span>
          )}
        </div>
      )}
      {!isExternal && skill.dependencies.length > 0 && (
        <div className="skill-dependencies">
          <small>Deps: {skill.dependencies.join(', ')}</small>
        </div>
      )}
    </div>
  );

  const renderLoadingSkeleton = () => (
    <div className="skill-grid">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <div key={i} className="skill-card skeleton">
          <div className="skeleton-header">
            <div className="skeleton-checkbox"></div>
            <div className="skeleton-title"></div>
          </div>
          <div className="skeleton-description"></div>
          <div className="skeleton-meta"></div>
        </div>
      ))}
    </div>
  );

  const builtinCount = AVAILABLE_SKILLS.length;

  return (
    <div className="skill-selector">
      <h2>Select Skills</h2>
      <p className="subtitle">Choose the capabilities your app should have</p>

      {/* Search Bar */}
      <div className="skill-search">
        <input
          type="text"
          placeholder="Search skills..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="search-input"
        />
        <span className="search-icon">🔍</span>
      </div>

      {/* Tab Navigation */}
      <div className="skill-tabs">
        <button
          className={`tab-button ${activeTab === 'builtin' ? 'active' : ''}`}
          onClick={() => handleTabChange('builtin')}
        >
          Built-in Skills ({builtinCount})
        </button>
        <button
          className={`tab-button ${activeTab === 'external' ? 'active' : ''}`}
          onClick={() => handleTabChange('external')}
        >
          External Skills (skills.sh)
        </button>
      </div>

      <div className="selected-count">
        <strong>{selectedSkills.length}</strong> skills selected
      </div>

      {/* Built-in Skills Tab */}
      {activeTab === 'builtin' && (
        <div className="builtin-skills">
          {categories.map(category => {
            const skills = getSkillsByCategory(category.key);
            if (skills.length === 0) return null;

            return (
              <div key={category.key} className="skill-category">
                <div className="category-header">
                  <span className="category-icon">{getCategoryIcon(category.key)}</span>
                  <div className="category-info">
                    <h3>{category.label}</h3>
                    <p>{category.description}</p>
                  </div>
                </div>
                <div className="skill-grid">
                  {skills.map(skill => renderSkillCard(skill, false))}
                </div>
              </div>
            );
          })}

          {filteredBuiltinSkills.length === 0 && (
            <div className="no-results">
              <p>No built-in skills match your search.</p>
            </div>
          )}
        </div>
      )}

      {/* External Skills Tab */}
      {activeTab === 'external' && (
        <div className="external-skills">
          {externalError && (
            <div className="error-message">
              <p>Failed to load external skills: {externalError}</p>
              <button onClick={retry} className="retry-button">
                Retry
              </button>
            </div>
          )}

          {!externalError && externalSkills.length === 0 && !externalLoading && (
            <div className="no-results">
              <p>No external skills found{searchTerm ? ` for "${searchTerm}"` : ''}.</p>
            </div>
          )}

          {categories.map(category => {
            const skills = getExternalSkillsByCategory(category.key);
            if (skills.length === 0) return null;

            return (
              <div key={category.key} className="skill-category">
                <div className="category-header">
                  <span className="category-icon">{getCategoryIcon(category.key)}</span>
                  <div className="category-info">
                    <h3>{category.label}</h3>
                    <p>{category.description}</p>
                  </div>
                </div>
                <div className="skill-grid">
                  {skills.map(skill => renderSkillCard(skill, true))}
                </div>
              </div>
            );
          })}

          {externalLoading && renderLoadingSkeleton()}

          {!externalLoading && hasMore && externalSkills.length > 0 && (
            <div className="load-more-container">
              <button onClick={loadMore} className="load-more-button">
                Load More Skills
              </button>
            </div>
          )}
        </div>
      )}

      {selectedSkills.length > 0 && (
        <div className="selected-skills-summary">
          <h3>Selected Skills</h3>
          <div className="selected-skills-list">
            {selectedSkills.map(skill => (
              <span
                key={skill.id}
                className={`skill-tag ${skill.id.startsWith('ext-') ? 'external' : ''}`}
              >
                {skill.name}
                {skill.id.startsWith('ext-') && <span className="tag-badge">ext</span>}
                <button onClick={() => onSkillToggle(skill.id)}>×</button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillSelector;
